/**
 * How an English interface groups the numbers in its catalogue strings
 * (locale audit FE-DT-08, waves W04 and V).
 *
 * Catalogue numbers ({n, number}, and # in a plural) are formatted for the
 * interface language, and "en" alone means en-US grouping. The rest of a page
 * formats with a bare toLocaleString(), which uses the JavaScript engine's
 * default locale. On an en-IN browser that put "138,000 selected" in a table
 * footer beside "1,38,000" in the page above it. So an English interface
 * groups catalogue numbers exactly as the page does: in the default locale,
 * always in Latin digits (format-message adds `-u-nu-latn`).
 *
 * The default locale, not navigator.language: Firefox takes navigator.language
 * from its content-language list but formats with its own locale (or the
 * system's regional format), and an English interface on a Hindi browser
 * (hi-IN) prints 1,38,000 too, which no English-only rule would see.
 */

/**
 * The locale a bare toLocaleString() formats numbers in: the engine's default
 * locale, without extensions ('en-IN-u-nu-deva' -> 'en-IN'; the digits are
 * chosen by format-message, never by the browser). Null where Intl cannot say.
 */
export function defaultNumberLocale(): string | null {
  try {
    const locale = new Intl.NumberFormat().resolvedOptions().locale
    return locale ? new Intl.Locale(locale).baseName : null
  } catch {
    return null
  }
}

// Grouping, decimal mark and a large enough value to show Indian grouping.
const SAMPLE = 1234567.891

/**
 * Whether `tag` writes numbers differently from plain English in Latin digits
 * ('en-IN' and 'hi-IN': 12,34,567.891; 'fr-FR': 1 234 567,891). en-US, en-GB
 * and ar-AE write them the same, so the provider keeps plain English for them
 * and does not re-render the page after hydration for nothing.
 */
export function numbersDifferFromEnglish(tag: string): boolean {
  try {
    return (
      new Intl.NumberFormat(`${tag}-u-nu-latn`).format(SAMPLE) !== new Intl.NumberFormat('en-u-nu-latn').format(SAMPLE)
    )
  } catch {
    return false
  }
}
